import React, { useState } from "react";
import {
  IResourceComponentsProps,
  BaseRecord,
  CrudFilters,
  useTranslate,
  usePermissions,
  useExport,
} from "@refinedev/core";
import {
  useTable,
  List,
  ShowButton,
  ExportButton,
  DeleteButton,
} from "@refinedev/antd";
import { Table, Space, Tag, Button, Typography, message, Input } from "antd";
import { EditOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { GlassCard } from "../../shared/components";
import { ProductCreateModal } from "./ProductCreateModal";
import { ProductEditModal } from "./ProductEditModal";
import { ProductImportModal } from "./ProductImportModal";
import { UserRole } from "../../models/role";

export const ProductList: React.FC<IResourceComponentsProps & { hideHeader?: boolean }> = ({
  hideHeader,
}) => {
  const _t = useTranslate();
  const { tableProps, tableQueryResult, setFilters, filters } = useTable({
    syncWithLocation: true,
    resource: "products",
  });

  const { triggerExport, isLoading: exportLoading } = useExport({
    resource: "products",
    meta: { paginate: false },
    mapData: (item) => ({
      name: item.name ?? "",
      code: item.code ?? "",
      type: item.type ? item.type : "",
      price: item.price ?? "",
      group: item.group ?? "",
    }),
    exportOptions: { filename: "produits" },
    onError: () => {
      message.error(_t("products.errors.exportFailed"));
    },
  });

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingProductId, setEditingProductId] = useState<
    number | string | null
  >(null);
  const { data: role } = usePermissions<string>();
  const canDeleteProduct =
    role === String(UserRole.admin) || role === String(UserRole.superAdmin);

  const { Text } = Typography;

  return (
    <div className="max-w-7xl mx-auto">
      <List
        resource="products"
        title={hideHeader ? false : undefined}
        breadcrumb={hideHeader ? false : undefined}
        headerButtons={() => [
          <ExportButton
            key="export"
            onClick={triggerExport}
            loading={exportLoading}
          >
            {_t("products.actions.exportCsv")}
          </ExportButton>,
          <Button
            key="import"
            icon={<UploadOutlined />}
            onClick={() => setImportModalOpen(true)}
          >
            {_t("products.actions.importCsv")}
          </Button>,
          <Button
            key="create"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalOpen(true)}
          >
            {_t("products.titles.create")}
          </Button>,
        ]}
      >
        <style>{`
                .products-list-table-view .ant-table { background: transparent !important; }
                .products-list-table-view .ant-table-thead > tr > th { background: transparent !important; }
                .products-list-table-view .ant-table-tbody > tr > td { background: transparent !important; }
            `}</style>
        <GlassCard className="products-list-table-view !p-0 overflow-hidden">
          <Table {...tableProps} rowKey="id">
            <Table.Column
              dataIndex="name"
              title={_t("products.fields.name")}
              filterDropdown={(props) => {
                const currentNameFilter = filters?.find(
                  (filter) => "field" in filter && filter.field === "name",
                );
                const currentNameValue =
                  currentNameFilter && "value" in currentNameFilter
                    ? (currentNameFilter.value as string)
                    : "";
                const hasSelectedNameKey =
                  props.selectedKeys && props.selectedKeys.length > 0;

                if (!hasSelectedNameKey && currentNameValue) {
                  props.setSelectedKeys([currentNameValue]);
                }

                const selectedNameValue = hasSelectedNameKey
                  ? ((props.selectedKeys?.[0] as string | undefined) ?? "")
                  : currentNameValue;

                const applyNameFilter = () => {
                  const selectedFilterValue =
                    (props.selectedKeys?.[0] as string | undefined) ?? "";
                  const otherFilters =
                    filters?.filter(
                      (filter) => !("field" in filter && filter.field === "name"),
                    ) || [];
                  const newFilters: CrudFilters = [...otherFilters];

                  if (selectedFilterValue.trim()) {
                    newFilters.push({
                      field: "name",
                      operator: "eq",
                      value: selectedFilterValue.trim(),
                    });
                  }

                  setFilters(newFilters);
                  props.confirm();
                };

                const resetNameFilter = () => {
                  const otherFilters =
                    filters?.filter(
                      (filter) => !("field" in filter && filter.field === "name"),
                    ) || [];

                  setFilters(otherFilters);
                  props.setSelectedKeys([""]);
                  props.clearFilters?.();
                  props.confirm();
                };

                return (
                  <div className="p-2 w-64">
                    <Input
                      value={selectedNameValue}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        props.setSelectedKeys([event.target.value])
                      }
                      onPressEnter={applyNameFilter}
                      placeholder={_t("products.fields.name")}
                      allowClear
                    />
                    <div className="flex gap-2 mt-2">
                      <Button
                        size="small"
                        type="primary"
                        onClick={applyNameFilter}
                        className="flex-1"
                      >
                        {_t("buttons.filter")}
                      </Button>
                      <Button
                        size="small"
                        onClick={resetNameFilter}
                        className="flex-1"
                      >
                        {_t("buttons.clear")}
                      </Button>
                    </div>
                  </div>
                );
              }}
              render={(name: string, record: BaseRecord) => (
                <Space direction="vertical" size={0}>
                  <Text strong>{name}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    #{record.id}
                  </Text>
                </Space>
              )}
            />
            <Table.Column dataIndex="code" title={_t("products.fields.code")} />
            <Table.Column
              dataIndex="type"
              title={_t("products.fields.type")}
              key="type"
              render={(type: string) => (
                <Tag color={type === "base" ? "blue" : "green"}>
                  {_t(`products.fields.typeValues.${type}`)}
                </Tag>
              )}
            />
            <Table.Column
              dataIndex="group"
              title={_t("products.fields.group")}
            />
            <Table.Column
              dataIndex="price"
              title={_t("products.fields.price")}
            />
            <Table.Column
              title={_t("table.actions")}
              dataIndex="actions"
              render={(_, record: BaseRecord) => (
                <Space>
                  <Button
                    icon={<EditOutlined />}
                    size="small"
                    onClick={() => {
                      setEditingProductId(record.id as number | string);
                      setEditModalOpen(true);
                    }}
                  />
                  <ShowButton hideText size="small" recordItemId={record.id} />
                  {canDeleteProduct && (
                    <DeleteButton
                      hideText
                      size="small"
                      recordItemId={record.id}
                      resource="products"
                    />
                  )}
                </Space>
              )}
            />
          </Table>
        </GlassCard>
        <ProductImportModal
          open={importModalOpen}
          onCancel={() => setImportModalOpen(false)}
          onSuccess={() => {
            setImportModalOpen(false);
            tableQueryResult?.refetch();
          }}
        />
        <ProductCreateModal
          open={createModalOpen}
          onCancel={() => setCreateModalOpen(false)}
          onSuccess={() => {
            setCreateModalOpen(false);
            tableQueryResult?.refetch();
          }}
        />
        <ProductEditModal
          open={editModalOpen}
          productId={editingProductId}
          onCancel={() => {
            setEditModalOpen(false);
            setEditingProductId(null);
          }}
          onSuccess={() => {
            setEditModalOpen(false);
            setEditingProductId(null);
            tableQueryResult?.refetch();
          }}
        />
      </List>
    </div>
  );
};
